import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PerformanceMetrics from './components/PerformanceMetrics';
import EquityCurveChart from './components/EquityCurveChart';
import TradeLogsTable from './components/TradeLogsTable';
import BotConfiguration from './components/BotConfiguration';
import PerformanceAnalytics from './components/PerformanceAnalytics';
import ActionButtons from './components/ActionButtons';

const BotDetails = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Mock bot data
  const botData = {
    id: 'bot_001',
    name: 'Golden Cross Momentum',
    creator: 'CryptoMaster Pro',
    description: `Advanced momentum trading strategy utilizing Golden Cross signals combined with RSI divergence patterns. This bot identifies optimal entry points when the 50-day moving average crosses above the 200-day moving average, while confirming momentum through RSI analysis.\n\nThe strategy includes sophisticated risk management with dynamic position sizing based on market volatility and implements trailing stop-losses to maximize profits during trending markets.`,
    version: '2.1.4',
    status: 'active',
    roi: '24.8%',
    totalTrades: 1247,
    winRate: '68.5%',
    maxDrawdown: '-8.5%',
    sharpeRatio: 2.85,
    createdAt: '2024-01-15',
    lastUpdated: '2024-09-10',
    tags: ['Momentum', 'Moving Average', 'RSI', 'Trend Following'],
    exchanges: ['Binance', 'KuCoin', 'Bybit'],
    tradingPairs: ['BTC/USDT', 'ETH/USDT', 'BNB/USDT', 'ADA/USDT']
  };

  // Mock equity curve data
  const equityData = [
    { date: '2024-01-01', balance: 10000, pnl: 0, roi: 0 },
    { date: '2024-01-15', balance: 10250, pnl: 250, roi: 2.5 },
    { date: '2024-02-01', balance: 10180, pnl: 180, roi: 1.8 },
    { date: '2024-02-15', balance: 10420, pnl: 420, roi: 4.2 },
    { date: '2024-03-01', balance: 10680, pnl: 680, roi: 6.8 },
    { date: '2024-03-15', balance: 10590, pnl: 590, roi: 5.9 },
    { date: '2024-04-01', balance: 10850, pnl: 850, roi: 8.5 },
    { date: '2024-04-15', balance: 11120, pnl: 1120, roi: 11.2 },
    { date: '2024-05-01', balance: 10980, pnl: 980, roi: 9.8 },
    { date: '2024-05-15', balance: 11340, pnl: 1340, roi: 13.4 },
    { date: '2024-06-01', balance: 11680, pnl: 1680, roi: 16.8 },
    { date: '2024-06-15', balance: 11520, pnl: 1520, roi: 15.2 },
    { date: '2024-07-01', balance: 11890, pnl: 1890, roi: 18.9 },
    { date: '2024-07-15', balance: 12150, pnl: 2150, roi: 21.5 },
    { date: '2024-08-01', balance: 11980, pnl: 1980, roi: 19.8 },
    { date: '2024-08-15', balance: 12320, pnl: 2320, roi: 23.2 },
    { date: '2024-09-01', balance: 12480, pnl: 2480, roi: 24.8 },
    { date: '2024-09-10', balance: 12480, pnl: 2480, roi: 24.8 }
  ];

  // Mock trade logs data
  const tradesData = [
    {
      id: 'trade_001',
      date: '2024-09-10',
      time: '14:32:15',
      pair: 'BTC/USDT',
      side: 'BUY',
      entryPrice: 58420,
      exitPrice: 59180,
      quantity: '0.1712',
      duration: 145,
      pnl: 130.05,
      pnlPercent: 1.3
    },
    {
      id: 'trade_002',
      date: '2024-09-10',
      time: '11:18:42',
      pair: 'ETH/USDT',
      side: 'BUY',
      entryPrice: 2340,
      exitPrice: 2385,
      quantity: '4.2735',
      duration: 89,
      pnl: 192.31,
      pnlPercent: 1.9
    },
    {
      id: 'trade_003',
      date: '2024-09-09',
      time: '16:45:23',
      pair: 'BNB/USDT',
      side: 'SELL',
      entryPrice: 542,
      exitPrice: 535,
      quantity: '18.52',
      duration: 67,
      pnl: -129.64,
      pnlPercent: -1.3
    },
    {
      id: 'trade_004',
      date: '2024-09-09',
      time: '09:22:11',
      pair: 'ADA/USDT',
      side: 'BUY',
      entryPrice: 0.342,
      exitPrice: 0.351,
      quantity: '2847.22',
      duration: 234,
      pnl: 25.63,
      pnlPercent: 2.6
    },
    {
      id: 'trade_005',
      date: '2024-09-08',
      time: '13:56:38',
      pair: 'BTC/USDT',
      side: 'BUY',
      entryPrice: 57890,
      exitPrice: 58650,
      quantity: '0.1725',
      duration: 178,
      pnl: 131.10,
      pnlPercent: 1.3
    }
  ];

  // Mock bot configuration
  const botConfig = {
    strategyType: 'Golden Cross Momentum',
    signalSource: 'Technical Analysis',
    entryConditions: 'MA50 > MA200 + RSI > 50 + Volume > 1.5x Average',
    exitConditions: 'RSI < 30 OR Stop Loss OR Take Profit',
    indicators: [
      {
        name: 'Moving Average (50)',
        period: '50 periods',
        parameters: 'Simple MA, Close price'
      },
      {
        name: 'Moving Average (200)',
        period: '200 periods',
        parameters: 'Simple MA, Close price'
      },
      {
        name: 'RSI',
        period: '14 periods',
        parameters: 'Overbought: 70, Oversold: 30'
      },
      {
        name: 'Volume Profile',
        period: '24 hours',
        parameters: 'Average volume threshold: 1.5x'
      }
    ],
    positionSizeMethod: 'Fixed Percentage',
    riskPerTrade: '2%',
    maxPositionSize: '25%',
    minPositionSize: '1%',
    stopLoss: '3%',
    takeProfit: '6%',
    maxDailyLoss: '5%',
    maxDrawdown: '10%',
    tradingPairs: [
      { symbol: 'BTC/USDT', base: 'BTC', status: 'active', minNotional: '10', maxNotional: '50000' },
      { symbol: 'ETH/USDT', base: 'ETH', status: 'active', minNotional: '10', maxNotional: '30000' },
      { symbol: 'BNB/USDT', base: 'BNB', status: 'active', minNotional: '10', maxNotional: '20000' },
      { symbol: 'ADA/USDT', base: 'ADA', status: 'inactive', minNotional: '10', maxNotional: '15000' }
    ],
    primaryExchange: 'Binance',
    tradingMode: 'Spot Trading',
    orderType: 'Market Orders',
    tradingHours: '24/7',
    timezone: 'UTC',
    weekendTrading: true,
    holidayTrading: true,
    signalInterval: '5 minutes',
    orderUpdateFreq: '1 minute',
    rebalanceFreq: 'Daily',
    highVolMode: 'Reduce position size',
    lowLiquidityMode: 'Skip trades',
    newsEventPause: false
  };

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleAction = (action, data) => {
    console.log(`Action: ${action}`, data);
    
    switch (action) {
      case 'deploy':
        // Navigate to deployment confirmation or dashboard
        navigate('/dashboard');
        break;
      case 'backtest':
        // Navigate to backtesting engine with pre-filled data
        navigate('/backtesting-engine', { state: { botId: data?.botId } });
        break;
      case 'copy':
        // Navigate to bot creation wizard with copied configuration
        navigate('/bot-creation-wizard', { state: { copyFrom: data?.botId } });
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        
        
       
          <div className="p-6">
            <div className="flex items-center justify-center min-h-96">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading bot details...</p>
              </div>
            </div>
          </div>
        
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
     
        <div className="p-6">
          {/* Breadcrumb */}
          <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-6">
            <button 
              onClick={() => navigate('/dashboard/bot-marketplace')}
              className="hover:text-foreground trading-transition"
            >
              Bot Marketplace
            </button>
            <Icon name="ChevronRight" size={16} />
            <span className="text-foreground">{botData?.name}</span>
          </div>

          {/* Bot Header */}
          <div className="bg-card border border-border rounded-2xl p-6 mb-8">
            <div className="flex flex-col lg:flex-row lg:items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                    <Icon name="Bot" size={24} className="text-primary" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-foreground">{botData?.name}</h1>
                    <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                      <span>by {botData?.creator}</span>
                      <span>•</span>
                      <span>v{botData?.version}</span>
                      <span>•</span>
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-success rounded-full"></div>
                        <span className="capitalize">{botData?.status}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <p className="text-muted-foreground mb-4 leading-relaxed">
                  {botData?.description?.split('\n')?.map((paragraph, index) => (
                    <span key={index}>
                      {paragraph}
                      {index < botData?.description?.split('\n')?.length - 1 && <br />}
                    </span>
                  ))}
                </p>

                <div className="flex flex-wrap gap-2 mb-4">
                  {botData?.tags?.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex items-center space-x-6 text-sm">
                  <div className="flex items-center space-x-2">
                    <Icon name="Calendar" size={16} className="text-muted-foreground" />
                    <span className="text-muted-foreground">Created: {botData?.createdAt}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="RefreshCw" size={16} className="text-muted-foreground" />
                    <span className="text-muted-foreground">Updated: {botData?.lastUpdated}</span>
                  </div>
                </div>
              </div>

              <div className="mt-6 lg:mt-0 lg:ml-6">
                <div className="flex flex-col space-y-3">
                  <Button
                    variant="default"
                    onClick={() => handleAction('deploy', { botId: botData?.id })}
                    iconName="Rocket"
                    iconPosition="left"
                  >
                    Deploy Bot
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleAction('backtest', { botId: botData?.id })}
                    iconName="BarChart3"
                    iconPosition="left"
                  >
                    Custom Backtest
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => handleAction('copy', { botId: botData?.id })}
                    iconName="Copy"
                    iconPosition="left"
                  >
                    Copy Configuration
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Performance Metrics */}
          <PerformanceMetrics botData={botData} />

          {/* Equity Curve Chart */}
          <EquityCurveChart equityData={equityData} />

          {/* Performance Analytics */}
          <PerformanceAnalytics analyticsData={{}} />

          {/* Trade Logs Table */}
          <TradeLogsTable trades={tradesData} />

          {/* Bot Configuration */}
          <BotConfiguration botConfig={botConfig} />

          {/* Action Buttons */}
          {/* <ActionButtons botData={botData} onAction={handleAction} /> */}
        </div>
     
    </div>
  );
};

export default BotDetails;